import { api } from './client'
import { operatorPortalConfig } from '../lib/operatorPortalConfig'

/** Sync a richiesta dei portali (`/api/sync-portali`): lettura immediata di stati e pratiche, oltre ai giri periodici. */
export interface SyncPortaleStato {
  gestore: string
  richiestaIl: string | null
  avviataIl: string | null
  eseguitaIl: string | null
  esito: string | null
  pausaFino: string | null
  inCorso: boolean
}

export const syncPortaliApi = {
  list: () => api<SyncPortaleStato[]>('/api/sync-portali'),
  get: (gestore: string) => api<SyncPortaleStato>(`/api/sync-portali/${gestore}`),
  request: (gestore: string) => api<SyncPortaleStato>(`/api/sync-portali/${gestore}`, { method: 'POST' }),
  /** "Sync tutti i portali": tutti i gestori letti dal server in una richiesta. */
  requestAll: () => api<SyncPortaleStato[]>('/api/sync-portali', { method: 'POST' }),
  /** Coraly: tutte le card collegate, lette dal CRM stesso. */
  coralyAll: () => api<{ cards: number }>('/api/coraly/sync-all', { method: 'POST' }),
  /** Coraly si legge dal CRM stesso: sync della card collegata alla pratica. */
  coraly: (contractId: number) => api<unknown>(`/api/contracts/${contractId}/coraly/sync`, { method: 'POST' }),
  /** Ultime righe del log dello script sul server (solo amministratori): conteggi e nomi di campi, mai dati dei clienti. */
  log: (gestore: 'ermes' | 'richieste', righe = 80) =>
    api<{ file: string; righe: string[]; aggiornato?: string; nota?: string }>(`/api/sync-portali/${gestore}/log?righe=${righe}`),
  /** Giro di esplorazione Ermes in sola lettura (nomi di campi e richieste nel log), entro un minuto. */
  esploraErmes: () => api<{ richiestaIl: string; nota: string }>('/api/sync-portali/ermes/esplora', { method: 'POST' }),
}

export const orario = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

/** Chiede il sync e aspetta la fine (al massimo `maxMs`), interrogando ogni 5 secondi. */
export async function syncAndWait(gestore: string, maxMs = 15 * 60_000, onTick?: (s: SyncPortaleStato) => void): Promise<SyncPortaleStato> {
  let s = await syncPortaliApi.request(gestore)
  onTick?.(s)
  const end = Date.now() + maxMs
  while (s.inCorso && Date.now() < end) {
    await new Promise(r => setTimeout(r, 5000))
    s = await syncPortaliApi.get(gestore)
    onTick?.(s)
  }
  return s
}

/** Portale da cui il "Sync" legge stato e dati della pratica (null = nessuna lettura automatica). */
export function syncGestoreFor(operatorName: string | undefined, operatorId: number | null | undefined): 'ermes' | 'teamwork' | 'coraly' | null {
  if (operatorId === 18 || /ermes/i.test(operatorName ?? '')) return 'ermes'
  const config = operatorPortalConfig(operatorName, operatorId ?? null)
  if (config.signedPda || config.profile?.portal.startsWith('Teamwork')) return 'teamwork'
  if (config.profile?.portal.startsWith('Coraly')) return 'coraly'
  return null
}
